<script>
  import UI from "./UI.svelte";
  import {
    getGroupsFromCountries,
    prepareScatterData,
    prepareLineData,
    prepareBenchmarkData,
    prepareMapData,
    prepareBarData,
    scatterToDownloadData,
    lineToDownloadData,
    benchmarkToDownloadData,
    mapToDownloadData,
    barToDownloadData,
  } from "./utils/dataPrep";
  import ScatterPlot from "./components/ScatterPlot.svelte";
  import LineChart from "./components/LineChart.svelte";
  import Benchmark from "./components/Benchmark.svelte";
  import Map from "./components/Map.svelte";
  import BarChart from "./components/BarChart.svelte";
  import DownloadLinks from "./components/DownloadLinks.svelte";
  import CountryProfile from "./components/CountryProfile.svelte";

  //Default settings
  let activeChartType = "scatter";
  let xVariable = {
    value: "ngdpc_usd",
    label: "GDP per capita (current US$)",
  };
  $: xTitle = xVariable.label;
  $: yTitle = yVariable.label;
  let yVariable = {
    value: "life",
    label: "Life expectancy at birth, total (years)",
  };
  let benchmarkValue = [xVariable, yVariable];

  let activeYear = 2020;
  let xAxisTitle = activeChartType === "scatter" ? "Test" : "Year";
  $: if (activeChartType === "line") {
    xAxisTitle = "Year";
    yAxisTitle = yTitle;
  } else if (activeChartType === "scatter") {
    xAxisTitle = xTitle;
    yAxisTitle = yTitle;
  } else if (activeChartType === "map") {
    legendLabel = yTitle;
  }
  let xMin;
  let xMax;
  let xLog = false;
  let yAxisTitle = yVariable.label;
  let yMin;
  let yMax;
  let yLog = false;
  let dotRadius = 5;
  let dotOpacity = 0.7;
  let dotColor = "#3f61da";
  let chartTitle = "";
  let xReference = null;
  let yReference = null;
  let activeGroup = { value: "all", label: "All countries" };
  let highlightGroup = { value: "all", label: "All countries" };
  let mostRecent = false;
  let primaryCountry = null;
  let referenceCountries = [];
  let showDots = false;
  let showOutlines = true;
  let reversePalette = false;
  let paletteMin;
  let paletteMax;
  let legendLabel = "Life expectancy at birth, total (years)";
  let paletteValue = { value: "interpolateBlues", label: "Blues" };
  let regressionLine = false;

  //Fetch basic data
  let countryData = {};
  fetch("../data/country_info.json")
    .then((res) => res.json())
    .then((data) => {
      countryData = data;
    })
    .catch((err) => console.error(err));

  $: groupsOptions = getGroupsFromCountries(countryData);

  let variableData = [];
  fetch("../data/variable_info.json")
    .then((res) => res.json())
    .then((data) => {
      variableData = data
        .filter((d) => d.type !== "parent")
        .filter((d) => d.source != "NA")
        .map((d) => {
          return {
            value: d.name,
            label: d.label + ' - ' + d.name,
            source: d.source,
            link: d.link,
          };
        });
    })
    .catch((err) => console.error(err));

  //Fetch scatterplot data
  let xData = [];
  let xLoading = true;
  $: fetch(`../data/variables/${xVariable.value}.json`)
    .then((res) => res.json())
    .then((data) => {
      xData = data;
      bmData[0] = data;
      xLoading = false;
    })
    .catch((err) => console.error(err));

  let yData = [];
  let yLoading = true;
  $: fetch(`../data/variables/${yVariable.value}.json`)
    .then((res) => res.json())
    .then((data) => {
      yData = data;
      bmData[1] = data;
      yLoading = false;
    })
    .catch((err) => console.error(err));

  $: scatterLoading = xLoading && yLoading;

  //Data prep
  let scatterData = [];
  $: scatterData = prepareScatterData(
    xData,
    yData,
    activeYear,
    countryData,
    activeGroup,
    mostRecent
  );

  let lineData = [];
  $: if (activeChartType) {
    lineData = prepareLineData(yData, countryData, activeGroup);
  }

  $: bmData =
    benchmarkValue === null
      ? []
      : benchmarkValue.map((d, i) => {
          return fetch(`../data/variables/${d.value}.json`)
            .then((res) => res.json())
            .then((data) => {
              bmData[i] = data;
            })
            .catch((err) => console.error(err));
        });
  $: benchmarkLoading = bmData.map((d) => typeof d.then === "function");

  $: benchmarkData = prepareBenchmarkData(
    bmData,
    activeYear,
    countryData,
    activeGroup,
    mostRecent
  );

  $: mapData = prepareMapData(
    yData,
    activeYear,
    countryData,
    mostRecent,
    activeGroup
  );

  $: barData = prepareBarData(
    yData,
    activeYear,
    countryData,
    mostRecent,
    referenceCountries,
    primaryCountry
  );

  $: downloadData =
    activeChartType === "scatter"
      ? scatterToDownloadData(scatterData, xVariable, yVariable)
      : activeChartType === "line"
      ? lineToDownloadData(lineData, yVariable)
      : activeChartType === "benchmark"
      ? benchmarkToDownloadData(benchmarkData, benchmarkValue)
      : activeChartType === "map"
      ? mapToDownloadData(mapData, yVariable)
      : activeChartType === "bar"
      ? barToDownloadData(barData, yVariable)
      : [];

  $: dataSources =
    activeChartType === "scatter" && variableData.length > 0
      ? [xVariable, yVariable].map((d) => {
          return {
            value: d.value,
            label: d.label,
            source: variableData.find((v) => v.value === d.value).source,
            url: variableData.find((v) => v.value === d.value).link,
          };
        })
      : activeChartType === "benchmark" && variableData.length > 0 && benchmarkValue
      ? benchmarkValue.map((d) => {
          return {
            value: d.value,
            label: d.label,
            source: variableData.find((v) => v.value === d.value).source,
            url: variableData.find((v) => v.value === d.value).link,
          };
        })
      : yVariable && variableData.length > 0 && benchmarkValue !== null
      ? [yVariable].map((d) => {
          return {
            value: d.value,
            label: d.label,
            source: variableData.find((v) => v.value === d.value).source,
            url: variableData.find((v) => v.value === d.value).link,
          };
        })
      : [];

  // For switching from a chart type to a line chart with reference countries and primary country
  $: if (activeChartType === "line" && primaryCountry) {
    /*referenceCountries.map((d) => {
      d.values = lineData.find((cntr) => cntr.label === d.label).values;
    });*/
    primaryCountry.values = lineData.find(
      (cntr) => cntr.label === primaryCountry.label
    ).values;
    primaryCountry.x = scatterData.find((cntr) => cntr.label === primaryCountry.label)
      ? scatterData.find((cntr) => cntr.label === primaryCountry.label).x
      : null;
    primaryCountry.y = scatterData.find((cntr) => cntr.label === primaryCountry.label) 
      ? scatterData.find((cntr) => cntr.label === primaryCountry.label).y
      : null;
  }
  $: if (activeChartType === "line" && referenceCountries.length > 0) {
    referenceCountries.map((d) => {
      d.values = lineData.find((cntr) => cntr.label === d.label).values;
    });
  }
  $: if (activeChartType === "scatter" && primaryCountry) {
    primaryCountry.x = scatterData.find((cntr) => cntr.label === primaryCountry.label)
      ? scatterData.find((cntr) => cntr.label === primaryCountry.label).x
      : null;
    primaryCountry.y = scatterData.find((cntr) => cntr.label === primaryCountry.label)
      ? scatterData.find((cntr) => cntr.label === primaryCountry.label).y
      : null;
  }
</script>

<div class="app-container">
  <UI
    variableOptions={variableData}
    bind:activeChartType
    bind:xVariable
    bind:yVariable
    bind:benchmarkValue
    bind:activeYear
    bind:mostRecent
    bind:xAxisTitle
    bind:xMin
    bind:xMax
    bind:xLog
    bind:yAxisTitle
    bind:yMin
    bind:yMax
    bind:yLog
    bind:dotRadius
    bind:dotOpacity
    bind:dotColor
    bind:chartTitle
    bind:xReference
    bind:yReference
    {groupsOptions}
    bind:activeGroup
    bind:highlightGroup
    bind:primaryCountry
    bind:referenceCountries
    bind:showDots
    bind:showOutlines
    bind:reversePalette
    bind:paletteMin
    bind:paletteMax
    bind:legendLabel
    bind:paletteValue
    bind:regressionLine
    {scatterData}
    {lineData}
  />
  {#if activeChartType !== "countryprofile"}
    <div class="viz-container">
      {#if activeChartType === "scatter"}
        <ScatterPlot
          {scatterData}
          {xAxisTitle}
          {xMin}
          {xMax}
          {xLog}
          {yAxisTitle}
          {yMin}
          {yMax}
          {yLog}
          {dotRadius}
          {dotOpacity}
          {dotColor}
          {chartTitle}
          {xReference}
          {yReference}
          {primaryCountry}
          {referenceCountries}
          {highlightGroup}
          {xVariable}
          {yVariable}
          {scatterLoading}
          {regressionLine}
        />
      {/if}
      {#if activeChartType === "line"}
        <LineChart
          {lineData}
          {xAxisTitle}
          {xMin}
          {xMax}
          xLog={false}
          {yAxisTitle}
          {yMin}
          {yMax}
          {yLog}
          {dotRadius}
          {dotOpacity}
          {dotColor}
          {chartTitle}
          {xReference}
          {yReference}
          {primaryCountry}
          {referenceCountries}
          {highlightGroup}
          xVariable="Year"
          {yVariable}
          {showDots}
        />
      {/if}
      {#if activeChartType === "benchmark"}
        <Benchmark
          {benchmarkValue}
          {benchmarkData}
          {xAxisTitle}
          {yAxisTitle}
          {dotRadius}
          {dotOpacity}
          {dotColor}
          {chartTitle}
          {xReference}
          {yReference}
          {primaryCountry}
          {referenceCountries}
          {highlightGroup}
          {xVariable}
          {yVariable}
          {benchmarkLoading}
        />
      {/if}
      {#if activeChartType === "bar"}
        <BarChart
          {barData}
          {chartTitle}
          {referenceCountries}
          {primaryCountry}
        />
      {/if}
      {#if activeChartType === "map"}
        <Map
          {mapData}
          {yVariable}
          {chartTitle}
          {primaryCountry}
          {showOutlines}
          {paletteMin}
          {paletteMax}
          {reversePalette}
          {legendLabel}
          {paletteValue}
        />
      {/if}
      <DownloadLinks {downloadData} {dataSources}/>
      <p>Sources:<br/>
        {#each dataSources as src}
          {src.label}: <a href={src.url} target="_blank">{src.source}</a><br/>
        {/each}
      </p>
    </div>
  {/if}
  {#if activeChartType === "countryprofile"}
    <div class="viz-container">
      <CountryProfile {primaryCountry} {countryData} />
    </div>
  {/if}
</div>

<style>
  @import url("https://fonts.googleapis.com/css?family=Roboto:400,700&display=swap");
  :global(*) {
    box-sizing: border-box;
    padding: 0;
    margin: 0;
  }
  :global(body) {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    padding: 0;
    margin: 0;
  }
  .app-container {
    display: flex;
    overflow: hidden;
  }
  .viz-container {
    min-height: min-content;
    padding: 12px;
    position: fixed;
    top: 0;
    left: 400px;
  }
</style>
